/**
 * New versions of the site. The service worker keeps a copy of the site so it opens fast (and offline), so the first
 * visit after a deploy shows that old copy while the new one downloads: no Google button yet, or "page not found" for a
 * page the deploy added. When the new version takes over, the page reloads into it: straight away if nothing has been
 * typed on this page, otherwise at the next move to another page, so a half-filled form is never lost.
 */
const RELOADED_AT = 'one10:reloaded-at';
const CHECK_EVERY = 5 * 60_000;

let typed = false;
let waiting = false;

/** Reloads, at most once a minute: a page that fails for another reason (offline) mustn't keep reloading. */
function reloadOnce(): boolean {
  try {
    if (Date.now() - Number(sessionStorage.getItem(RELOADED_AT) || 0) < 60_000) return false;
    sessionStorage.setItem(RELOADED_AT, String(Date.now()));
  } catch { /* storage unavailable: reload anyway */ }
  location.reload();
  return true;
}

export function startUpdates() {
  // A page of the old version that wasn't loaded yet is gone from the site after a deploy: load the new version
  // instead of a blank screen.
  window.addEventListener('vite:preloadError', (e) => {
    if (reloadOnce()) e.preventDefault();
  });
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;

  document.addEventListener('input', () => { typed = true; }, true);
  let controller = navigator.serviceWorker.controller;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    const before = controller;
    controller = navigator.serviceWorker.controller;
    // The first visit installs the worker: nothing to reload.
    if (!before) return;
    if (typed) waiting = true;
    else location.reload();
  });

  const register = () => {
    void navigator.serviceWorker.register('/sw.js', { scope: '/' }).then((reg) => {
      // The app on a phone's home screen comes back from the background without loading again: look for a new
      // version then, too.
      let checked = Date.now();
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible' || Date.now() - checked < CHECK_EVERY) return;
        checked = Date.now();
        void reg.update().catch(() => { /* offline */ });
      });
    }).catch(() => { /* no service worker (private mode): the site still works */ });
  };
  if (document.readyState === 'complete') register();
  else window.addEventListener('load', register, { once: true });
}

/** The app moved to another page: a new version that waited for this loads now. */
export function pageChanged() {
  typed = false;
  if (waiting) location.reload();
}
